import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  Badge,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  CardActions,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Paper,
  Backdrop,
} from '@mui/material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { ReusableDataGrid, CustomDrawer, ActionsMenu, type ActionMenuItem } from '@/components/common';
import {
  FilterList as FilterIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Assignment as StatusIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Receipt as InvoiceIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik, FieldArray, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { RootState, AppDispatch } from '@/store/types';
import { useAuth } from '@/hooks';
import {
  fetchPurchaseOrders,
  createPurchaseOrder,
  updatePurchaseOrder,
  deletePurchaseOrder,
  fetchPurchaseOrderById,
  fetchCustomers,
  fetchCompanies,
  // fetchMaterials,
  fetchMaterialsByGst,
  fetchWorkProcessSummary,
  createWorkProcess,
  updateWorkProcess,
} from '@/store';
import { PurchaseOrderFormData, purchaseOrderService } from '@/services/purchaseOrder/purchaseOrderService';
import moment from 'moment';
import { USER_ROLES } from '@/constants';
import toast from 'react-hot-toast';
import { EditableInvoiceForm } from '@/components/invoice/EditableInvoiceForm';

const validationSchema = Yup.object({
  customerId: Yup.string().required('Customer is required'),
  companyId: Yup.string().required('Company is required'),
  poNumber: Yup.string().required('PO Number is required').min(3, 'PO Number must be at least 3 characters'),
  poDateTime: Yup.string().required('PO Date & Time is required'),
  jobType: Yup.string().required('Job Type is required'),
  punching: Yup.boolean(),
  bending: Yup.boolean(),
  welding: Yup.boolean(),
  powder: Yup.boolean(),
  assembly: Yup.boolean(),
  isActive: Yup.boolean(),
  remarks: Yup.string(),
});

// const workProcessValidationSchema = Yup.object({
//   startDateTime: Yup.string().required('Start date time is required' ),
//   endDateTime: Yup.string().when('status', {
//     is: 'completed',
//     then: (schema) => schema.required('End date time is required for completed status'),
//     otherwise: (schema) => schema
//   }),
//   status: Yup.string().oneOf(['pending', 'progress', 'completed'], 'Invalid status').required('Status is required'),
//   remarks: Yup.string(),
//   materialsUsed: Yup.array().of(
//     Yup.object({
//       materialId: Yup.string().required('Material is required'),
//       quantityCount: Yup.number().min(0, 'Quantity count must be positive'),
//       quantityKg: Yup.number().min(0, 'Quantity kg must be positive'),
//     })
//   ),
//   // Fields for dispatch (level 6)
//   dispatchedDateTime: Yup.string(),
//   vehicleNumber: Yup.string(),
//   invoiceNumber: Yup.string(),
//   invoiceDate: Yup.string(),
// });

const isLevel6 = (level: number | string | undefined) =>
  Number(level) === 6;
// const isLevel1 = (level: number | string | undefined) =>
//   Number(level) === 1 && Number(level)!==6;
export const workProcessValidationSchema = Yup.object({
  // level: Yup.number().required(),

  startDateTime: Yup.string().when('level', {
    is: (l: number | string | undefined) => !isLevel6(l),
    then: (s) => s.required('Start date time is required'),
  }),

  status: Yup.string().when('level', {
    is: (l: number | string | undefined) => !isLevel6(l),
    then: (s) =>
      s.oneOf(['pending', 'progress', 'completed'])
       .required('Status is required'),
  }),

  endDateTime: Yup.string().when(['level', 'status'], {
    is: (
      l: number | string | undefined,
      st: 'pending' | 'progress' | 'completed' | undefined
    ) => !isLevel6(l) && st === 'completed',
    then: (s) => s.required('End date time is required'),
  }),

  remarks: Yup.string(),

  materialsUsed: Yup.array().when('level', {
    is: (l: number | string | undefined) => !isLevel6(l),
    then: (s) =>
      s.of(
        Yup.object({
          materialId: Yup.string().required('Material is required'),
          quantityCount: Yup.number().min(0),
          quantityKg: Yup.number().min(0),
        })
      ),
  }),

  dispatchedDateTime: Yup.string().when('level', {
    is: (l: number | string | undefined) => isLevel6(l),
    then: (s) => s.required('Dispatched date time is required'),
  }),

  vehicleNumber: Yup.string().when('level', {
    is: (l: number | string | undefined) => isLevel6(l),
    then: (s) => s.required('Vehicle number is required'),
  }),

  invoiceNumber: Yup.string().when('level', {
    is: (l: number | string | undefined) => isLevel6(l),
    then: (s) => s.required('Invoice number is required'),
  }),

  invoiceDate: Yup.string().when('level', {
    is: (l: number | string | undefined) => isLevel6(l),
    then: (s) => s.required('Invoice date is required'),
  }),
});

const initialFormData: PurchaseOrderFormData = {
  customerId: '',
  companyId: '',
  poNumber: '',
  poDateTime: moment().format('YYYY-MM-DDTHH:mm'),
  punching: true,
  bending: true,
  welding: true,
  powder: true,
  assembly: true,
  isActive: true,
  remarks: '',
  jobType:'purchase'

};

const initialWorkProcessFormData = {
  startDateTime: moment(new Date()).format('YYYY-MM-DDTHH:mm').toString(),
  endDateTime: '',
  status: 'pending',
  remarks: '',
  materialsUsed: [{ materialId: '', quantityCount: 0, quantityKg: 0 }],
  // Fields for dispatch (level 6)
  dispatchedDateTime: '',
  vehicleNumber: '',
  invoiceNumber: '',
  invoiceDate: '',
  level:0
};

export const ManagePurchaseOrder: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const { purchaseOrders, purchaseOrdersLoading, isLoading, pagination, selectedPurchaseOrder, selectedPurchaseOrderLoading } = useSelector(
    (state: RootState) => state.purchaseOrder
  );
  const { customers } = useSelector((state: RootState) => state.customer);
  const { companies } = useSelector((state: RootState) => state.company);
  const { materials } = useSelector((state: RootState) => state.material);
  const { workProcessSummary, workProcessSummaryLoading } = useSelector(
    (state: RootState) => state.workProcess
  );

  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedPOForStatus, setSelectedPOForStatus] = useState<any>(null);
  const [workProcessFormOpen, setWorkProcessFormOpen] = useState(false);
  const [selectedWorkProcessLevel, setSelectedWorkProcessLevel] = useState<number>(1);
  const [editingWorkProcess, setEditingWorkProcess] = useState<any>(null);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | ''>('');
  const [customerIdFilter, setCustomerIdFilter] = useState<string>('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [apiLoading, setApiLoading] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedPOForInvoice, setSelectedPOForInvoice] = useState<any>(null);
  const [comprehensivePOData, setComprehensivePOData] = useState<any>(null);
  const [existingInvoiceData, setExistingInvoiceData] = useState<any>(null);

  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema,
    onSubmit: async (values) => {
      try {
        const dataWithUserId = {
          ...values,
          userId: user?._id || user?.id
        };
        
        if (editingId) {
          await dispatch(updatePurchaseOrder({
            id: editingId,
            data: dataWithUserId
          }));
        } else {
          await dispatch(createPurchaseOrder(dataWithUserId));
        }
        handleCloseDrawer();
        loadData();
      } catch (error) {
        console.error('Error saving purchase order:', error);
      }
    },
  });

  const handleWorkProcessSubmit = async (data: any) => {
    try {
      // Prepare data with conditional endDateTime
      let processedData = { ...data };
      
      // Only include endDateTime if status is completed
      if (data.status !== 'completed') {
        delete processedData.endDateTime;
      }
      if(selectedWorkProcessLevel?.toString() !=='6'){
       
        delete processedData.dispatchedDateTime
        delete processedData.vehicleNumber
        delete processedData.invoiceNumber
        delete processedData.invoiceDate
              delete processedData.level
      }
       if(selectedWorkProcessLevel?.toString() ==='6'){
       processedData = { ...processedData , status:'completed'};
        delete processedData.startDateTime
        delete processedData.materialsUsed
        // delete processedData.invoiceNumber
        delete processedData.level
        
      }
      console.log(editingWorkProcess)
      if (editingWorkProcess) {
        await dispatch(updateWorkProcess({
          level: selectedWorkProcessLevel,
          id: editingWorkProcess._id || editingWorkProcess._id ,
          data: {
            ...processedData,
            userId: user?._id || user?.id
          }
        }));
      } else {
        await dispatch(createWorkProcess({
          level: selectedWorkProcessLevel,
          data: {
            ...processedData,
            poId: selectedPOForStatus._id,
            companyId: selectedPOForStatus.companyId?._id || selectedPOForStatus.companyId,
            userId: user?._id || user?.id
          }
        }));
      }
      setWorkProcessFormOpen(false);
      workProcessFormik.resetForm();
      // Refresh the work process summary
      if (selectedPOForStatus) {
        await dispatch(fetchWorkProcessSummary(selectedPOForStatus._id));
      }
    } catch (error) {
      console.error('Error saving work process:', error);
    }
  };

  const workProcessFormik = useFormik({
    initialValues: initialWorkProcessFormData,
    validationSchema: workProcessValidationSchema,
    onSubmit: handleWorkProcessSubmit,
  });
useEffect(()=>{
if(selectedWorkProcessLevel){
  workProcessFormik.setFieldValue('level',selectedWorkProcessLevel)
}
},[selectedWorkProcessLevel])
  const loadData = useCallback(() => {
    dispatch(fetchPurchaseOrders({ 
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      isActive: isActiveFilter !== '' ? isActiveFilter : undefined,
      customerId: customerIdFilter || undefined
    }));
  }, [dispatch, paginationModel, isActiveFilter, customerIdFilter]);

  const loadCustomers = useCallback(() => {
    dispatch(fetchCustomers({ limit: 100 }));
  }, [dispatch]);

  const loadCompanies = useCallback(() => {
    dispatch(fetchCompanies({ limit: 100 }));
  }, [dispatch]);

  const loadMaterialsByGst = useCallback((gst: string, companyId: string, jobType?: string) => {
    if (gst && companyId) {
      dispatch(fetchMaterialsByGst({
        gst,
        companyId,
        page: 1,
        limit: 1000,
        isActive: true,
        jobType
      }));
    }
  }, [dispatch]);

  useEffect(() => {
    loadData();
    loadCustomers();
    loadCompanies();
  }, [loadData, loadCustomers, loadCompanies]);

  const handleOpenDrawer = (purchaseOrder?: any) => {
    if (purchaseOrder) {
      formik.setValues({
        customerId: purchaseOrder.customerId?._id || '',
        companyId: purchaseOrder.companyId?._id || '',
        poNumber: purchaseOrder.poNumber,
        poDateTime: moment(purchaseOrder.poDateTime).local().format('YYYY-MM-DDTHH:mm'),
        punching: purchaseOrder.punching,
        bending: purchaseOrder.bending,
        welding: purchaseOrder.welding,
        powder: purchaseOrder.powder,
        assembly: purchaseOrder.assembly,
        isActive: purchaseOrder.isActive,
        remarks: purchaseOrder.remarks || '',
        jobType: purchaseOrder.jobType||''
      });
      setEditingId(purchaseOrder._id);
    } else {
      formik.resetForm();
      setEditingId(null);
    }
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    formik.resetForm();
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await dispatch(deletePurchaseOrder(id));
      setDeleteConfirmId(null);
      loadData();
    } catch (error) {
      console.error('Error deleting purchase order:', error);
    }
  };

  const clearFilters = () => {
    setIsActiveFilter('');
    setCustomerIdFilter('');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (isActiveFilter !== '') count++;
    if (customerIdFilter) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'default';
  };

  const getCustomerName = (customerId: any) => {
    if (!customerId || typeof customerId !== 'object') return 'N/A';
    return customerId.companyName || 'N/A';
  };

  const getCompanyName = (companyId: any) => {
    if (!companyId || typeof companyId !== 'object') return 'N/A';
    return companyId.name || 'N/A';
  };

  // const getServicesChips = (row: any) => {
  //   const services = [];
  //   if (row.punching) services.push('Punching');
  //   if (row.bending) services.push('Bending');
  //   if (row.welding) services.push('Welding');
  //   if (row.powder) services.push('Powder');
  //   if (row.assembly) services.push('Assembly');
    
  //   return services.map((service, index) => (
  //     <Chip 
  //       key={index} 
  //       label={service} 
  //       size="small" 
  //       variant="outlined" 
  //       sx={{ mr: 0.5, mb: 0.5 }}
  //     />
  //   ));
  // };

  const handleViewPurchaseOrder = async (purchaseOrder: any) => {
    try {
      setApiLoading(true);
      await dispatch(fetchPurchaseOrderById(purchaseOrder._id));
      setViewDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching purchase order details:', error);
    } finally {
      setApiLoading(false);
    }
  };

  const handleStatusClick = async (purchaseOrder: any) => {
    try {
      setApiLoading(true);
      setSelectedPOForStatus(purchaseOrder);
      await dispatch(fetchWorkProcessSummary(purchaseOrder._id));
      setStatusDialogOpen(true);
    } catch (error) {
      console.error('Error fetching work process summary:', error);
    } finally {
      setApiLoading(false);
    }
  };

  const handleInvoiceClick = async (purchaseOrder: any) => {
    try {
      setApiLoading(true);
      // First check if invoices exist for this purchase order
      const invoicesResponse = await purchaseOrderService.getInvoicesByPurchaseOrder(purchaseOrder._id);

      if (invoicesResponse.success && invoicesResponse.data && invoicesResponse.data.length > 0) {
        // Invoices exist - use the first invoice data to populate the form
        const existingInvoice = invoicesResponse.data[0];
        setExistingInvoiceData(existingInvoice);
        setComprehensivePOData(null);
        setSelectedPOForInvoice(existingInvoice.purchaseOrderId);
        setInvoiceDialogOpen(true);
      } else {
        // No invoices - fetch comprehensive purchase order data including materials
        const response = await purchaseOrderService.getComprehensivePurchaseOrder(purchaseOrder._id);
        if (response.success) {
          setExistingInvoiceData(null);
          setComprehensivePOData(response.data);
          setSelectedPOForInvoice(response.data.purchaseOrder);
          setInvoiceDialogOpen(true);
        } else {
          toast.error('Failed to fetch purchase order details');
        }
      }
    } catch (error) {
      console.error('Error fetching purchase order for invoice:', error);
      toast.error('Error fetching purchase order details');
    } finally {
      setApiLoading(false);
    }
  };

  const handleCreateWorkProcess = (level: number) => {
    workProcessFormik.resetForm();
    workProcessFormik.setFieldValue('level',level)
    setSelectedWorkProcessLevel(level);
    setEditingWorkProcess(null);

    // Load materials by GST and companyId when opening work process form
    if (selectedPOForStatus) {
      const customerGst = selectedPOForStatus.companyId?.gst;
      const companyId = selectedPOForStatus.companyId?._id || selectedPOForStatus.companyId;
      const jobType = selectedPOForStatus.jobType;
      if (customerGst && companyId) {
        loadMaterialsByGst(customerGst, companyId, jobType);
      }
    }

    setWorkProcessFormOpen(true);
  };

  const handleEditWorkProcess = (level: number, workProcess: any) => {
    setSelectedWorkProcessLevel(level);
    setEditingWorkProcess(workProcess);
    workProcessFormik.setFieldValue('level',level)

    // Load materials by GST and companyId when opening work process form
    if (selectedPOForStatus) {
      const customerGst = selectedPOForStatus.customerId?.gst;
      const companyId = selectedPOForStatus.companyId?._id || selectedPOForStatus.companyId;
      const jobType = selectedPOForStatus.jobType;

      if (customerGst && companyId) {
        loadMaterialsByGst(customerGst, companyId, jobType);
      }
    }

    workProcessFormik.setValues({
      level:level,
      startDateTime: workProcess.startDateTime ? moment(workProcess.startDateTime).local().format('YYYY-MM-DDTHH:mm') : '',
      endDateTime: workProcess.endDateTime ? moment(workProcess.endDateTime).local().format('YYYY-MM-DDTHH:mm') : '',
      status: workProcess.status || 'pending',
      remarks: workProcess.remarks || '',
      materialsUsed: workProcess.materialsUsed?.map((material: any) => ({
        materialId: material.materialId?._id || material.materialId || '',
        quantityCount: material.quantityCount || 0,
        quantityKg: material.quantityKg || 0
      })) || [{ materialId: '', quantityCount: 0, quantityKg: 0 }],
      dispatchedDateTime: workProcess.dispatchedDateTime ? moment(workProcess.dispatchedDateTime).local().format('YYYY-MM-DDTHH:mm') : '',
      vehicleNumber: workProcess.vehicleNumber || '',
      invoiceNumber: workProcess.invoiceNumber || '',
      invoiceDate: workProcess.invoiceDate ? moment(workProcess.invoiceDate).local().format('YYYY-MM-DD') : '',
    });
    setWorkProcessFormOpen(true);
  };

  // const getTotalPages = () => {
  //   if (!pagination) return 1;
  //   return pagination.totalPages;
  // };

  // const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
  //   setPaginationModel(prev => ({ ...prev, page: page - 1 }));
  // };

  const handleTablePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPaginationModel(prev => ({ ...prev, page: newPage }));
  };

  const handleTableRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPaginationModel(prev => ({ ...prev, pageSize: newPageSize, page: 0 }));
  };

  // Purchase Order Card Component
  const PurchaseOrderCard = ({ purchaseOrder }: { purchaseOrder: any }) => (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h6" component="div" fontWeight="bold" color="primary">
            {purchaseOrder.poNumber}
          </Typography>

        </Box>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Chip
            // label={purchaseOrder.isActive ? 'Active' : 'Inactive'}
      label={`${purchaseOrder?.currentProcess} - ${purchaseOrder?.processStatus}`}

            color={getStatusColor(purchaseOrder.isActive) as any}
            size="small"
          />
          </Box>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Customer
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {getCustomerName(purchaseOrder.customerId)}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Company
            </Typography>
            <Typography variant="body2">
              {getCompanyName(purchaseOrder.companyId)}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              PO Date
            </Typography>
            <Typography variant="body2">
              {moment(purchaseOrder.poDateTime).local().format('DD/MM/YYYY hh:mm A')}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Job Type
            </Typography>
            <Chip
              label={purchaseOrder.jobType === 'purchase' ? 'Sales' : purchaseOrder.jobType === 'jobwork' ? 'Jobwork' : purchaseOrder.jobType || 'N/A'}
              color={purchaseOrder.jobType === 'purchase' ? 'primary' : 'secondary'}
              size="small"
              variant="outlined"
            />
          </Box>

          {purchaseOrder.remarks && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Remarks
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {purchaseOrder.remarks}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box>
           {user?.role!==USER_ROLES.EMPLOYEE  &&<Button
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewPurchaseOrder(purchaseOrder)}
            sx={{ mr: 1 }}
          >
            View
          </Button>}
          <Button
            size="small"
            startIcon={<StatusIcon />}
            onClick={() => handleStatusClick(purchaseOrder)}
            color="primary"
            sx={{ mr: 1 }}
          >
            Status
          </Button>
          <Button
            size="small"
            startIcon={<InvoiceIcon />}
            onClick={() => handleInvoiceClick(purchaseOrder)}
            color="secondary"
          >
            Invoice
          </Button>
        </Box>
       {user?.role!==USER_ROLES.EMPLOYEE  &&  <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenDrawer(purchaseOrder)}
            disabled={isLoading}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteConfirmId(purchaseOrder._id)}
            disabled={isLoading}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>}
      </CardActions>
    </Card>
  );

  // Cards View Component
  const PurchaseOrderCardsView = () => (
    <Box>
      {purchaseOrdersLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : purchaseOrders && purchaseOrders.length > 0 ? (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {purchaseOrders.map((purchaseOrder) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={purchaseOrder._id}>
                <PurchaseOrderCard purchaseOrder={purchaseOrder} />
              </Grid>
            ))}
          </Grid>
          
          {pagination && pagination.totalPages >= 1 && (
            <Box display="flex" justifyContent="end" mt={2}>
              <TablePagination
                component="div"
                count={pagination.totalDocs || 0}
                page={paginationModel.page}
                onPageChange={handleTablePageChange}
                rowsPerPage={paginationModel.pageSize}
                onRowsPerPageChange={handleTableRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
              />
            </Box>
          )}
        </>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={300}
          textAlign="center"
        >
          <Typography variant="h6" color="text.secondary" mb={2}>
            No purchase orders found
          </Typography>
          <Button
            variant="contained"
            onClick={() => handleOpenDrawer()}
            disabled={isLoading}
          >
            Create Purchase Order
          </Button>
        </Box>
      )}
    </Box>
  );

  // Table View Component
  const PurchaseOrderTableView = () => (
    <ReusableDataGrid
      rows={purchaseOrders || []}
      columns={columns}
      getRowId={(row) => row?._id}
      loading={purchaseOrdersLoading}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      paginationData={pagination ? { totalRecords: pagination.totalDocs } : undefined}
      noRowsMessage="No purchase orders found"
      noRowsActionLabel="Create Purchase Order"
      onNoRowsAction={() => handleOpenDrawer()}
      onRowClick={(params) => handleViewPurchaseOrder(params.row)}
    />
  );

  const columns: GridColDef[] = [
    {
      field: 'poNumber',
      headerName: 'PO Number',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'customerId',
      headerName: 'Customer',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" color="primary">
          {getCustomerName(params.value)}
        </Typography>
      ),
    },
    {
      field: 'companyId',
      headerName: 'Company',
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant="body2">
          {getCompanyName(params.value)}
        </Typography>
      ),
    },
    {
      field: 'poDateTime',
      headerName: 'PO Date',
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant="body2">
          {moment(params.value).local().format('DD/MM/YYYY hh:mm A')}
        </Typography>
      ),
    },
    {
      field: 'jobType',
      headerName: 'Job Type',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 'purchase' ? 'Sales' : params.value === 'jobwork' ? 'Jobwork' : params.value || 'N/A'}
          color={params.value === 'purchase' ? 'primary' : 'secondary'}
          size="small"
          variant="outlined"
        />
      ),
    },
    // {
    //   field: 'services',
    //   headerName: 'Services',
    //   flex: 1.2,
    //   minWidth: 200,
    //   sortable: false,
    //   renderCell: (params) => (
    //     <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
    //       {getServicesChips(params.row)}
    //     </Box>
    //   ),
    // },
   {
  field: 'isActive',
  headerName: 'Status',
  flex: 0.6,
  minWidth: 100,
  renderCell: (params) => (
    <Chip
      label={`${params.row?.currentProcess} - ${params.row?.processStatus}`}
      color={getStatusColor(params.value) as any}
      size="small"
    />
  ),
}
,
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const actions: ActionMenuItem[] = [
          {
            id: 'status',
            title: 'Status',
            onClick: () => handleStatusClick(params.row),
            disabled: isLoading,
          },
          {
            id: 'invoice',
            title: 'Invoice',
            onClick: () => handleInvoiceClick(params.row),
            disabled: isLoading,
          },
          {
            id: 'edit',
            title: 'Edit',
            onClick: () => handleOpenDrawer(params.row),
            disabled: isLoading,
          },
          {
            id: 'delete',
            title: 'Delete',
            onClick: () => setDeleteConfirmId(params.row._id),
            disabled: isLoading,
          },
        ];

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <ActionsMenu
              actions={actions}
              disabled={isLoading}
            />
          </div>
        );
      },
    },
  ];
console.log(workProcessFormik.isValid)
console.log(workProcessFormik)
console.log(workProcessFormik.isSubmitting)

  // Helper function to calculate cumulative materials from previous levels
  const getMaterialsSummary = (currentLevel: number) => {
    if (!workProcessSummary) return null;

    const levelData = [
      workProcessSummary.punchingLaser[0],
      workProcessSummary.bendingMachine[0],
      workProcessSummary.weldingGrinding[0],
      workProcessSummary.powderCoating[0],
      workProcessSummary.assembly[0]
    ];

    // Get materials from previous levels
    const previousMaterials: { [key: string]: { materialId: any, materialName: string, quantityCount: number, quantityKg: number } } = {};

    for (let i = 0; i < currentLevel - 1; i++) {
      const level = levelData[i];
      if (level?.materialsUsed) {
        level.materialsUsed.forEach((material: any) => {
          const key = material.materialId?._id || material.materialId;
          if (previousMaterials[key]) {
            previousMaterials[key].quantityCount += material.quantityCount || 0;
            previousMaterials[key].quantityKg += material.quantityKg || 0;
          } else {
            previousMaterials[key] = {
              materialId: material.materialId,
              materialName: material.materialId?.materialName || 'Unknown Material',
              quantityCount: material.quantityCount || 0,
              quantityKg: material.quantityKg || 0
            };
          }
        });
      }
    }

    // Get materials from current level
    const currentLevelData = levelData[currentLevel - 1];
    const currentMaterials = currentLevelData?.materialsUsed || [];

    // Calculate total (cumulative)
    const totalMaterials = { ...previousMaterials };
    currentMaterials.forEach((material: any) => {
      const key = material.materialId?._id || material.materialId;
      if (totalMaterials[key]) {
        totalMaterials[key].quantityCount += material.quantityCount || 0;
        totalMaterials[key].quantityKg += material.quantityKg || 0;
      } else {
        totalMaterials[key] = {
          materialId: material.materialId,
          materialName: material.materialId?.materialName || 'Unknown Material',
          quantityCount: material.quantityCount || 0,
          quantityKg: material.quantityKg || 0
        };
      }
    });

    return {
      previousMaterials: Object.values(previousMaterials),
      currentMaterials,
      totalMaterials: Object.values(totalMaterials)
    };
  };

  return (
    <Box>
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h5" component="h5" flexGrow={1}>
            Manage Purchase Orders
          </Typography>
        </Box>
        <Divider />
      </Box>

      <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Grid size="auto">
          {user?.role!==USER_ROLES.EMPLOYEE  &&<ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="table" aria-label="table view">
              <ViewListIcon fontSize="small" />
              <Typography variant="body2" sx={{ ml: 1 }}>Table</Typography>
            </ToggleButton>
            <ToggleButton value="cards" aria-label="cards view">
              <ViewModuleIcon fontSize="small" />
              <Typography variant="body2" sx={{ ml: 1 }}>Cards</Typography>
            </ToggleButton>
          </ToggleButtonGroup>}
        </Grid>
        {user?.role!==USER_ROLES.EMPLOYEE  &&<Grid size="auto">
          <Stack direction="row" spacing={2}>
            <Badge badgeContent={activeFilterCount} color="primary">
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setFilterDrawerOpen(true)}
              >
                Filters
              </Button>
            </Badge>
            <Button
              variant="contained"
              onClick={() => handleOpenDrawer()}
              disabled={isLoading}
            >
              Add Purchase Order
            </Button>
          </Stack>
        </Grid>}
      </Grid>

      {viewMode === 'table' ? (
        <PurchaseOrderTableView />
      ) : (
        <PurchaseOrderCardsView />
      )}

      <CustomDrawer
        open={openDrawer}
        onClose={handleCloseDrawer}
        title={editingId ? 'Edit Purchase Order' : 'Create Purchase Order'}
        subtitle={editingId ? 'Update purchase order details' : 'Add a new purchase order to the system'}
        width={600}
        disableBackdropClick={true}

      >
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ py: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{xs: 12, sm: 12}}>
                <TextField
                  fullWidth
                  select
                  label="Customer"
                  name="customerId"
                  value={formik.values.customerId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.customerId && Boolean(formik.errors.customerId)}
                  helperText={formik.touched.customerId && formik.errors.customerId}
                  variant="outlined"
                >
                  <MenuItem value="">Select Customer</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer._id} value={customer._id}>
                      {customer.companyName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{xs: 12, sm: 12}}>
                <TextField
                  fullWidth
                  select
                  label="Company"
                  name="companyId"
                  value={formik.values.companyId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.companyId && Boolean(formik.errors.companyId)}
                  helperText={formik.touched.companyId && formik.errors.companyId}
                  variant="outlined"
                >
                  <MenuItem value="">Select Company</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company._id} value={company._id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="PO Number"
                  name="poNumber"
                  value={formik.values.poNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.poNumber && Boolean(formik.errors.poNumber)}
                  helperText={formik.touched.poNumber && formik.errors.poNumber}
                  placeholder="e.g., PO-2024-001"
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="PO Date & Time"
                  name="poDateTime"
                  type="datetime-local"
                  value={formik.values.poDateTime}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.poDateTime && Boolean(formik.errors.poDateTime)}
                  helperText={formik.touched.poDateTime && formik.errors.poDateTime}
                />
              </Grid>
              <Grid size={{xs: 12, sm: 12}}>
                <TextField
                  fullWidth
                  select
                  label="Job Type"
                  name="jobType"
                  required
                  value={formik.values.jobType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.jobType && Boolean(formik.errors.jobType)}
                  helperText={formik.touched.jobType && formik.errors.jobType}
                  variant="outlined"
                >
                  <MenuItem value="purchase">Sales</MenuItem>
                  <MenuItem value="jobwork">Jobwork</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{xs: 12}}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Services Required
                </Typography>
                <FormGroup>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 6, sm: 4}}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.punching}
                            onChange={formik.handleChange}
                            name="punching"
                          />
                        }
                        label="Punching"
                      />
                    </Grid>
                    <Grid size={{xs: 6, sm: 4}}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.bending}
                            onChange={formik.handleChange}
                            name="bending"
                          />
                        }
                        label="Bending"
                      />
                    </Grid>
                    <Grid size={{xs: 6, sm: 4}}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.welding}
                            onChange={formik.handleChange}
                            name="welding"
                          />
                        }
                        label="Welding"
                      />
                    </Grid>
                    <Grid size={{xs: 6, sm: 4}}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.powder}
                            onChange={formik.handleChange}
                            name="powder"
                          />
                        }
                        label="Powder Coating"
                      />
                    </Grid>
                    <Grid size={{xs: 6, sm: 4}}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.assembly}
                            onChange={formik.handleChange}
                            name="assembly"
                          />
                        }
                        label="Assembly"
                      />
                    </Grid>
                  </Grid>
                </FormGroup>
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  select
                  name="isActive"
                  label="Status"
                  value={formik.values.isActive}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.isActive && Boolean(formik.errors.isActive)}
                  helperText={formik.touched.isActive && formik.errors.isActive}
                  variant="outlined"
                  slotProps={{
                    select: {
                      native: true,
                    },
                  }}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </TextField>
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Remarks (Optional)"
                  name="remarks"
                  multiline
                  rows={3}
                  value={formik.values.remarks}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                  helperText={formik.touched.remarks && formik.errors.remarks}
                  placeholder="Additional notes about the purchase order..."
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCloseDrawer} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!formik.isValid || isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : editingId ? 'Update' : 'Create'}
            </Button>
          </Box>
        </form>
      </CustomDrawer>

      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this purchase order? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Purchase Order Details Dialog */}
      <Dialog 
        open={viewDetailsOpen} 
        onClose={() => setViewDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Purchase Order Details</Typography>
            {selectedPurchaseOrder && (
              <Chip
                label={selectedPurchaseOrder.isActive ? 'Active' : 'Inactive'}
                color={getStatusColor(selectedPurchaseOrder.isActive) as any}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPurchaseOrderLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : selectedPurchaseOrder ? (
            <Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      PO Number
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {selectedPurchaseOrder.poNumber}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      PO Date & Time
                    </Typography>
                    <Typography variant="body1">
                      {moment(selectedPurchaseOrder.poDateTime).local().format('DD/MM/YYYY hh:mm A')}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Customer
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {typeof selectedPurchaseOrder.customerId === 'object' ? selectedPurchaseOrder.customerId?.companyName : 'N/A'}
                    </Typography>
                    {typeof selectedPurchaseOrder.customerId === 'object' && selectedPurchaseOrder.customerId?.gst && (
                      <Typography variant="body2" color="text.secondary">
                        GST: {selectedPurchaseOrder.customerId.gst}
                      </Typography>
                    )}
                    {typeof selectedPurchaseOrder.customerId === 'object' && selectedPurchaseOrder.customerId?.email && (
                      <Typography variant="body2" color="text.secondary">
                        Email: {selectedPurchaseOrder.customerId.email}
                      </Typography>
                    )}
                    {typeof selectedPurchaseOrder.customerId === 'object' && selectedPurchaseOrder.customerId?.mobile && (
                      <Typography variant="body2" color="text.secondary">
                        Mobile: {selectedPurchaseOrder.customerId.mobile}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Company
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {typeof selectedPurchaseOrder.companyId === 'object' ? selectedPurchaseOrder.companyId?.name : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Services Required
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedPurchaseOrder.punching && (
                        <Chip label="Punching" size="small" variant="outlined" color="primary" />
                      )}
                      {selectedPurchaseOrder.bending && (
                        <Chip label="Bending" size="small" variant="outlined" color="primary" />
                      )}
                      {selectedPurchaseOrder.welding && (
                        <Chip label="Welding" size="small" variant="outlined" color="primary" />
                      )}
                      {selectedPurchaseOrder.powder && (
                        <Chip label="Powder Coating" size="small" variant="outlined" color="primary" />
                      )}
                      {selectedPurchaseOrder.assembly && (
                        <Chip label="Assembly" size="small" variant="outlined" color="primary" />
                      )}
                    </Box>
                  </Box>
                </Grid>

                {selectedPurchaseOrder.remarks && (
                  <Grid size={{ xs: 12 }}>
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Remarks
                      </Typography>
                      <Typography variant="body1">
                        {selectedPurchaseOrder.remarks}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" mb={2}>
                    Audit Information
                  </Typography>
                </Grid>

                {/* Created Information */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box mb={2} p={2} sx={{ bgcolor: 'success.lighter', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="success.dark" fontWeight="bold" gutterBottom>
                      Created By
                    </Typography>
                    {selectedPurchaseOrder.createdBy && (
                      <>
                        <Typography variant="body2" fontWeight="medium">
                          {selectedPurchaseOrder.createdBy.name || selectedPurchaseOrder.createdBy.email || 'N/A'}
                        </Typography>
                        {selectedPurchaseOrder.createdBy.empId && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Emp ID: {selectedPurchaseOrder.createdBy.empId}
                          </Typography>
                        )}
                        {/* {selectedPurchaseOrder.createdBy.email && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {selectedPurchaseOrder.createdBy.email}
                          </Typography>
                        )} */}
                        {/* {selectedPurchaseOrder.createdBy.mobile && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Mobile: {selectedPurchaseOrder.createdBy.mobile}
                          </Typography>
                        )} */}
                      </>
                    )}
                    <Typography variant="caption" color="success.dark" fontWeight="bold" display="block" sx={{ mt: 1 }}>
                      {moment(selectedPurchaseOrder.createdAt).local().format('DD/MM/YYYY hh:mm A')}
                    </Typography>
                  </Box>
                </Grid>

                {/* Updated Information */}
                {selectedPurchaseOrder.updatedBy && selectedPurchaseOrder.createdAt !== selectedPurchaseOrder.updatedAt && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box mb={2} p={2} sx={{ bgcolor: 'warning.lighter', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="warning.dark" fontWeight="bold" gutterBottom>
                        Last Updated By
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedPurchaseOrder.updatedBy.name || selectedPurchaseOrder.updatedBy.email || 'N/A'}
                      </Typography>
                      {selectedPurchaseOrder.updatedBy.empId && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Emp ID: {selectedPurchaseOrder.updatedBy.empId}
                        </Typography>
                      )}
                      {/* {selectedPurchaseOrder.updatedBy.email && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {selectedPurchaseOrder.updatedBy.email}
                        </Typography>
                      )}
                      {selectedPurchaseOrder.updatedBy.mobile && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Mobile: {selectedPurchaseOrder.updatedBy.mobile}
                        </Typography>
                      )} */}
                      <Typography variant="caption" color="warning.dark" fontWeight="bold" display="block" sx={{ mt: 1 }}>
                        {moment(selectedPurchaseOrder.updatedAt).local().format('DD/MM/YYYY hh:mm A')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Typography>No data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDetailsOpen(false)}>Close</Button>
          {selectedPurchaseOrder && (
            <Button
              variant="contained"
              onClick={() => {
                setViewDetailsOpen(false);
                handleOpenDrawer(selectedPurchaseOrder);
              }}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Work Process Status Dialog */}
      <Dialog 
        open={statusDialogOpen} 
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Work Process Status</Typography>
            {selectedPOForStatus && (
              <Chip
                label={selectedPOForStatus.poNumber}
                color="primary"
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {workProcessSummaryLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : workProcessSummary ? (
            <Box>
              {(() => {
                // Check if previous stages are completed
                const isPunchingCompleted = !selectedPOForStatus?.punching || (workProcessSummary.punchingLaser.length > 0 && workProcessSummary.punchingLaser[0].status === 'completed');
                const isBendingCompleted = !selectedPOForStatus?.bending || (workProcessSummary.bendingMachine.length > 0 && workProcessSummary.bendingMachine[0].status === 'completed');
                const isWeldingCompleted = !selectedPOForStatus?.welding || (workProcessSummary.weldingGrinding.length > 0 && workProcessSummary.weldingGrinding[0].status === 'completed');
                const isPowderCompleted = !selectedPOForStatus?.powder || (workProcessSummary.powderCoating.length > 0 && workProcessSummary.powderCoating[0].status === 'completed');
                const isAssemblyCompleted = !selectedPOForStatus?.assembly || (workProcessSummary.assembly.length > 0 && workProcessSummary.assembly[0].status === 'completed');

                return (
                  <>
                    {/* Punching Laser - Always show if required */}
                    {selectedPOForStatus?.punching && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">Punching Laser</Typography>
                          <Chip
                            label={workProcessSummary.punchingLaser.length > 0 ? workProcessSummary.punchingLaser[0].status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not Started'}
                            color={workProcessSummary.punchingLaser.length > 0 ? (workProcessSummary.punchingLaser[0].status === 'completed' ? 'success' : workProcessSummary.punchingLaser[0].status === 'progress' ? 'warning' : 'default') : 'default'}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        </AccordionSummary>
                        <AccordionDetails>
                          {workProcessSummary.punchingLaser.length > 0 ? (
                            <Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Status
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" color={workProcessSummary.punchingLaser[0].status === 'completed' ? 'success.main' : workProcessSummary.punchingLaser[0].status === 'progress' ? 'warning.main' : 'text.primary'}>
                                      {workProcessSummary.punchingLaser[0].status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Start Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.punchingLaser[0].startDateTime ? moment(workProcessSummary.punchingLaser[0].startDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not started'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      End Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.punchingLaser[0].endDateTime ? moment(workProcessSummary.punchingLaser[0].endDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not completed'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleEditWorkProcess(1, workProcessSummary.punchingLaser[0])}
                                      startIcon={<EditIcon />}
                                      disabled={workProcessSummary.punchingLaser[0].status === 'completed'&& user?.role !=='admin'}
                                    >
                                      Edit
                                    </Button>
                                  </Box>
                                </Grid>
                                {(() => {
                                  const materialsSummary = getMaterialsSummary(1);
                                  return materialsSummary && materialsSummary.currentMaterials.length > 0 && (
                                    <Grid size={{ xs: 12 }}>
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                          Materials Summary
                                        </Typography>

                                        {/* Materials Added in This Level */}
                                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                          <Typography variant="caption" color="primary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            Materials Added in This Level
                                          </Typography>
                                          {materialsSummary.currentMaterials.map((material: any, index: number) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                              <Typography variant="body2" fontWeight="medium">
                                                {material.materialId?.materialName || 'Unknown Material'}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Count: {material.quantityCount || 0}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Weight: {material.quantityKg || 0} kg
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>

                                        {/* Total Materials (same as current for Level 1) */}
                                        <Box sx={{ p: 1.5, bgcolor: 'success.lighter', borderRadius: 1 }}>
                                          <Typography variant="caption" color="success.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            Total Materials (Cumulative)
                                          </Typography>
                                          {materialsSummary.totalMaterials.map((material: any, index: number) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                              <Typography variant="body2" fontWeight="medium">
                                                {material.materialName}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Count: {material.quantityCount}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Weight: {material.quantityKg} kg
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    </Grid>
                                  );
                                })()}
                                {workProcessSummary.punchingLaser[0].remarks && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Remarks
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.punchingLaser[0].remarks}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                  {workProcessSummary.punchingLaser[0].createdBy && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Created by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.punchingLaser[0].createdBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.punchingLaser[0].createdBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                   {workProcessSummary.punchingLaser[0].updatedBy && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Updated by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.punchingLaser[0].updatedBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.punchingLaser[0].updatedBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                No work process entry found.
                              </Typography>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleCreateWorkProcess(1)}
                                startIcon={<AddIcon />}
                              >
                                Start Process
                              </Button>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Bending Machine - Show if required and punching is completed */}
                    {selectedPOForStatus?.bending && isPunchingCompleted && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">Bending Machine</Typography>
                          <Chip
                            label={workProcessSummary.bendingMachine.length > 0 ? workProcessSummary.bendingMachine[0].status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not Started'}
                            color={workProcessSummary.bendingMachine.length > 0 ? (workProcessSummary.bendingMachine[0].status === 'completed' ? 'success' : workProcessSummary.bendingMachine[0].status === 'progress' ? 'warning' : 'default') : 'default'}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        </AccordionSummary>
                        <AccordionDetails>
                          {workProcessSummary.bendingMachine.length > 0 ? (
                            <Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Status
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" color={workProcessSummary.bendingMachine[0].status === 'completed' ? 'success.main' : workProcessSummary.bendingMachine[0].status === 'progress' ? 'warning.main' : 'text.primary'}>
                                      {workProcessSummary.bendingMachine[0].status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Start Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.bendingMachine[0].startDateTime ? moment(workProcessSummary.bendingMachine[0].startDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not started'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      End Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.bendingMachine[0].endDateTime ? moment(workProcessSummary.bendingMachine[0].endDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not completed'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleEditWorkProcess(2, workProcessSummary.bendingMachine[0])}
                                      startIcon={<EditIcon />}
                                      disabled={workProcessSummary.bendingMachine[0].status === 'completed'&& user?.role !=='admin'}
                                    >
                                      Edit
                                    </Button>
                                  </Box>
                                </Grid>
                                {(() => {
                                  const materialsSummary = getMaterialsSummary(2);
                                  return materialsSummary && (materialsSummary.currentMaterials.length > 0 || materialsSummary.previousMaterials.length > 0) && (
                                    <Grid size={{ xs: 12 }}>
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                          Materials Summary
                                        </Typography>

                                        {/* Materials from Previous Levels */}
                                        {materialsSummary.previousMaterials.length > 0 && (
                                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                                            <Typography variant="caption" color="info.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                              Materials from Previous Levels
                                            </Typography>
                                            {materialsSummary.previousMaterials.map((material: any, index: number) => (
                                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {material.materialName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Count: {material.quantityCount}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Weight: {material.quantityKg} kg
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        )}

                                        {/* Materials Added in This Level */}
                                        {materialsSummary.currentMaterials.length > 0 && (
                                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                            <Typography variant="caption" color="primary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                              Materials Added in This Level
                                            </Typography>
                                            {materialsSummary.currentMaterials.map((material: any, index: number) => (
                                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {material.materialId?.materialName || 'Unknown Material'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Count: {material.quantityCount || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Weight: {material.quantityKg || 0} kg
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        )}

                                        {/* Total Materials */}
                                        <Box sx={{ p: 1.5, bgcolor: 'success.lighter', borderRadius: 1 }}>
                                          <Typography variant="caption" color="success.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            Total Materials (Cumulative)
                                          </Typography>
                                          {materialsSummary.totalMaterials.map((material: any, index: number) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                              <Typography variant="body2" fontWeight="medium">
                                                {material.materialName}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Count: {material.quantityCount}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Weight: {material.quantityKg} kg
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    </Grid>
                                  );
                                })()}
                                {workProcessSummary.bendingMachine[0].remarks && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Remarks
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.bendingMachine[0].remarks}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                 {workProcessSummary.bendingMachine[0].createdBy && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Created by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.bendingMachine[0].createdBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.bendingMachine[0].createdBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                {workProcessSummary.bendingMachine[0].updatedBy && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Updated by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.bendingMachine[0].updatedBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.bendingMachine[0].updatedBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                No work process entry found.
                              </Typography>

                              {/* Show Materials Summary from previous levels */}
                              {(() => {
                                const materialsSummary = getMaterialsSummary(2);
                                return materialsSummary && materialsSummary.previousMaterials.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                      Materials Summary (From Previous Levels)
                                    </Typography>
                                    <Box sx={{ p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                                      <Typography variant="caption" color="info.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                        Materials Available from Previous Levels
                                      </Typography>
                                      {materialsSummary.previousMaterials.map((material: any, index: number) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {material.materialName}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Count: {material.quantityCount}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Weight: {material.quantityKg} kg
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })()}

                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleCreateWorkProcess(2)}
                                startIcon={<AddIcon />}
                              >
                                Start Process
                              </Button>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Welding Grinding - Show if required and previous stages are completed */}
                    {selectedPOForStatus?.welding && isPunchingCompleted && isBendingCompleted && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">Welding & Grinding</Typography>
                          <Chip
                            label={workProcessSummary.weldingGrinding.length > 0 ? workProcessSummary.weldingGrinding[0].status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not Started'}
                            color={workProcessSummary.weldingGrinding.length > 0 ? (workProcessSummary.weldingGrinding[0].status === 'completed' ? 'success' : workProcessSummary.weldingGrinding[0].status === 'progress' ? 'warning' : 'default') : 'default'}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        </AccordionSummary>
                        <AccordionDetails>
                          {workProcessSummary.weldingGrinding.length > 0 ? (
                            <Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Status
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" color={workProcessSummary.weldingGrinding[0].status === 'completed' ? 'success.main' : workProcessSummary.weldingGrinding[0].status === 'progress' ? 'warning.main' : 'text.primary'}>
                                      {workProcessSummary.weldingGrinding[0].status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Start Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.weldingGrinding[0].startDateTime ? moment(workProcessSummary.weldingGrinding[0].startDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not started'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      End Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.weldingGrinding[0].endDateTime ? moment(workProcessSummary.weldingGrinding[0].endDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not completed'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleEditWorkProcess(3, workProcessSummary.weldingGrinding[0])}
                                      startIcon={<EditIcon />}
                                      disabled={workProcessSummary.weldingGrinding[0].status === 'completed'&& user?.role !=='admin'}
                                    >
                                      Edit
                                    </Button>
                                  </Box>
                                </Grid>
                                {(() => {
                                  const materialsSummary = getMaterialsSummary(3);
                                  return materialsSummary && (materialsSummary.currentMaterials.length > 0 || materialsSummary.previousMaterials.length > 0) && (
                                    <Grid size={{ xs: 12 }}>
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                          Materials Summary
                                        </Typography>

                                        {/* Materials from Previous Levels */}
                                        {materialsSummary.previousMaterials.length > 0 && (
                                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                                            <Typography variant="caption" color="info.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                              Materials from Previous Levels
                                            </Typography>
                                            {materialsSummary.previousMaterials.map((material: any, index: number) => (
                                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {material.materialName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Count: {material.quantityCount}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Weight: {material.quantityKg} kg
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        )}

                                        {/* Materials Added in This Level */}
                                        {materialsSummary.currentMaterials.length > 0 && (
                                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                            <Typography variant="caption" color="primary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                              Materials Added in This Level
                                            </Typography>
                                            {materialsSummary.currentMaterials.map((material: any, index: number) => (
                                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {material.materialId?.materialName || 'Unknown Material'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Count: {material.quantityCount || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Weight: {material.quantityKg || 0} kg
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        )}

                                        {/* Total Materials */}
                                        <Box sx={{ p: 1.5, bgcolor: 'success.lighter', borderRadius: 1 }}>
                                          <Typography variant="caption" color="success.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            Total Materials (Cumulative)
                                          </Typography>
                                          {materialsSummary.totalMaterials.map((material: any, index: number) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                              <Typography variant="body2" fontWeight="medium">
                                                {material.materialName}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Count: {material.quantityCount}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Weight: {material.quantityKg} kg
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    </Grid>
                                  );
                                })()}
                                {workProcessSummary.weldingGrinding[0].remarks && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Remarks
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.weldingGrinding[0].remarks}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                 {workProcessSummary.weldingGrinding[0].createdBy && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Created by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.weldingGrinding[0].createdBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.weldingGrinding[0].createdBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                {workProcessSummary.weldingGrinding[0].updatedBy && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Updated by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.weldingGrinding[0].updatedBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.weldingGrinding[0].updatedBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                No work process entry found.
                              </Typography>

                              {/* Show Materials Summary from previous levels */}
                              {(() => {
                                const materialsSummary = getMaterialsSummary(3);
                                return materialsSummary && materialsSummary.previousMaterials.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                      Materials Summary (From Previous Levels)
                                    </Typography>
                                    <Box sx={{ p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                                      <Typography variant="caption" color="info.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                        Materials Available from Previous Levels
                                      </Typography>
                                      {materialsSummary.previousMaterials.map((material: any, index: number) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {material.materialName}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Count: {material.quantityCount}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Weight: {material.quantityKg} kg
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })()}

                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleCreateWorkProcess(3)}
                                startIcon={<AddIcon />}
                              >
                                Start Process
                              </Button>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Powder Coating - Show if required and previous stages are completed */}
                    {selectedPOForStatus?.powder && isPunchingCompleted && isBendingCompleted && isWeldingCompleted && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">Powder Coating</Typography>
                          <Chip
                            label={workProcessSummary.powderCoating.length > 0 ? workProcessSummary.powderCoating[0].status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not Started'}
                            color={workProcessSummary.powderCoating.length > 0 ? (workProcessSummary.powderCoating[0].status === 'completed' ? 'success' : workProcessSummary.powderCoating[0].status === 'progress' ? 'warning' : 'default') : 'default'}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        </AccordionSummary>
                        <AccordionDetails>
                          {workProcessSummary.powderCoating.length > 0 ? (
                            <Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Status
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" color={workProcessSummary.powderCoating[0].status === 'completed' ? 'success.main' : workProcessSummary.powderCoating[0].status === 'progress' ? 'warning.main' : 'text.primary'}>
                                      {workProcessSummary.powderCoating[0].status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Start Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.powderCoating[0].startDateTime ? moment(workProcessSummary.powderCoating[0].startDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not started'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      End Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.powderCoating[0].endDateTime ? moment(workProcessSummary.powderCoating[0].endDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not completed'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleEditWorkProcess(4, workProcessSummary.powderCoating[0])}
                                      startIcon={<EditIcon />}
                                      disabled={workProcessSummary.powderCoating[0].status === 'completed'&& user?.role !=='admin'}
                                    >
                                      Edit
                                    </Button>
                                  </Box>
                                </Grid>
                                {(() => {
                                  const materialsSummary = getMaterialsSummary(4);
                                  return materialsSummary && (materialsSummary.currentMaterials.length > 0 || materialsSummary.previousMaterials.length > 0) && (
                                    <Grid size={{ xs: 12 }}>
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                          Materials Summary
                                        </Typography>

                                        {/* Materials from Previous Levels */}
                                        {materialsSummary.previousMaterials.length > 0 && (
                                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                                            <Typography variant="caption" color="info.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                              Materials from Previous Levels
                                            </Typography>
                                            {materialsSummary.previousMaterials.map((material: any, index: number) => (
                                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {material.materialName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Count: {material.quantityCount}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Weight: {material.quantityKg} kg
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        )}

                                        {/* Materials Added in This Level */}
                                        {materialsSummary.currentMaterials.length > 0 && (
                                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                            <Typography variant="caption" color="primary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                              Materials Added in This Level
                                            </Typography>
                                            {materialsSummary.currentMaterials.map((material: any, index: number) => (
                                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {material.materialId?.materialName || 'Unknown Material'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Count: {material.quantityCount || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Weight: {material.quantityKg || 0} kg
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        )}

                                        {/* Total Materials */}
                                        <Box sx={{ p: 1.5, bgcolor: 'success.lighter', borderRadius: 1 }}>
                                          <Typography variant="caption" color="success.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            Total Materials (Cumulative)
                                          </Typography>
                                          {materialsSummary.totalMaterials.map((material: any, index: number) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                              <Typography variant="body2" fontWeight="medium">
                                                {material.materialName}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Count: {material.quantityCount}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Weight: {material.quantityKg} kg
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    </Grid>
                                  );
                                })()}
                                {workProcessSummary.powderCoating[0].remarks && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Remarks
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.powderCoating[0].remarks}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                 {workProcessSummary.powderCoating[0].createdBy && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Created by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.powderCoating[0].createdBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.powderCoating[0].createdBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                  {workProcessSummary.powderCoating[0].updatedBy && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Updated by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.powderCoating[0].updatedBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.powderCoating[0].updatedBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                No work process entry found.
                              </Typography>

                              {/* Show Materials Summary from previous levels */}
                              {(() => {
                                const materialsSummary = getMaterialsSummary(4);
                                return materialsSummary && materialsSummary.previousMaterials.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                      Materials Summary (From Previous Levels)
                                    </Typography>
                                    <Box sx={{ p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                                      <Typography variant="caption" color="info.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                        Materials Available from Previous Levels
                                      </Typography>
                                      {materialsSummary.previousMaterials.map((material: any, index: number) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {material.materialName}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Count: {material.quantityCount}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Weight: {material.quantityKg} kg
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })()}

                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleCreateWorkProcess(4)}
                                startIcon={<AddIcon />}
                              >
                                Start Process
                              </Button>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Assembly - Show if required and previous stages are completed */}
                    {selectedPOForStatus?.assembly && isPunchingCompleted && isBendingCompleted && isWeldingCompleted && isPowderCompleted && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">Assembly</Typography>
                          <Chip
                            label={workProcessSummary.assembly.length > 0 ? workProcessSummary.assembly[0].status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not Started'}
                            color={workProcessSummary.assembly.length > 0 ? (workProcessSummary.assembly[0].status === 'completed' ? 'success' : workProcessSummary.assembly[0].status === 'progress' ? 'warning' : 'default') : 'default'}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        </AccordionSummary>
                        <AccordionDetails>
                          {workProcessSummary.assembly.length > 0 ? (
                            <Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Status
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" color={workProcessSummary.assembly[0].status === 'completed' ? 'success.main' : workProcessSummary.assembly[0].status === 'progress' ? 'warning.main' : 'text.primary'}>
                                      {workProcessSummary.assembly[0].status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Start Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.assembly[0].startDateTime ? moment(workProcessSummary.assembly[0].startDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not started'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      End Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.assembly[0].endDateTime ? moment(workProcessSummary.assembly[0].endDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not completed'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleEditWorkProcess(5, workProcessSummary.assembly[0])}
                                      startIcon={<EditIcon />}
                                      disabled={workProcessSummary.assembly[0].status === 'completed'&& user?.role !=='admin'}
                                    >
                                      Edit
                                    </Button>
                                  </Box>
                                </Grid>
                                {(() => {
                                  const materialsSummary = getMaterialsSummary(5);
                                  return materialsSummary && (materialsSummary.currentMaterials.length > 0 || materialsSummary.previousMaterials.length > 0) && (
                                    <Grid size={{ xs: 12 }}>
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                          Materials Summary
                                        </Typography>

                                        {/* Materials from Previous Levels */}
                                        {materialsSummary.previousMaterials.length > 0 && (
                                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                                            <Typography variant="caption" color="info.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                              Materials from Previous Levels
                                            </Typography>
                                            {materialsSummary.previousMaterials.map((material: any, index: number) => (
                                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {material.materialName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Count: {material.quantityCount}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Weight: {material.quantityKg} kg
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        )}

                                        {/* Materials Added in This Level */}
                                        {materialsSummary.currentMaterials.length > 0 && (
                                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                            <Typography variant="caption" color="primary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                              Materials Added in This Level
                                            </Typography>
                                            {materialsSummary.currentMaterials.map((material: any, index: number) => (
                                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {material.materialId?.materialName || 'Unknown Material'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Count: {material.quantityCount || 0}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Weight: {material.quantityKg || 0} kg
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        )}

                                        {/* Total Materials */}
                                        <Box sx={{ p: 1.5, bgcolor: 'success.lighter', borderRadius: 1 }}>
                                          <Typography variant="caption" color="success.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            Total Materials (Cumulative)
                                          </Typography>
                                          {materialsSummary.totalMaterials.map((material: any, index: number) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                              <Typography variant="body2" fontWeight="medium">
                                                {material.materialName}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Count: {material.quantityCount}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Weight: {material.quantityKg} kg
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    </Grid>
                                  );
                                })()}
                                {workProcessSummary.assembly[0].remarks && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Remarks
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.assembly[0].remarks}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )} {workProcessSummary.assembly[0].createdBy && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Created by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.assembly[0].createdBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.assembly[0].createdBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                {workProcessSummary.assembly[0].updatedBy && (
                                  <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Updated by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.assembly[0].updatedBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.assembly[0].updatedBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                No work process entry found.
                              </Typography>

                              {/* Show Materials Summary from previous levels */}
                              {(() => {
                                const materialsSummary = getMaterialsSummary(5);
                                return materialsSummary && materialsSummary.previousMaterials.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                      Materials Summary (From Previous Levels)
                                    </Typography>
                                    <Box sx={{ p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                                      <Typography variant="caption" color="info.dark" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                        Materials Available from Previous Levels
                                      </Typography>
                                      {materialsSummary.previousMaterials.map((material: any, index: number) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {material.materialName}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Count: {material.quantityCount}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Weight: {material.quantityKg} kg
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })()}

                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleCreateWorkProcess(5)}
                                startIcon={<AddIcon />}
                              >
                                Start Process
                              </Button>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Accounts Dispatch - Show only if all required previous stages are completed */}
                    {isPunchingCompleted && isBendingCompleted && isWeldingCompleted && isPowderCompleted && isAssemblyCompleted && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">Accounts & Dispatch</Typography>
                          <Chip
                            label={workProcessSummary.accountsDispatch ? 'Completed' : 'Not Started'}
                            color={workProcessSummary.accountsDispatch ? 'success' : 'default'}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        </AccordionSummary>
                        <AccordionDetails>
                          {workProcessSummary.accountsDispatch ? (
                            <Box>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Dispatched Time
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.accountsDispatch.dispatchedDateTime ? moment(workProcessSummary.accountsDispatch.dispatchedDateTime).local().format('DD/MM/YYYY hh:mm A') : 'Not dispatched'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Vehicle Number
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.accountsDispatch.vehicleNumber || 'N/A'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Invoice Number
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.accountsDispatch.invoiceNumber || 'N/A'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                      Invoice Date
                                    </Typography>
                                    <Typography variant="body1">
                                      {workProcessSummary.accountsDispatch.invoiceDate ? moment(workProcessSummary.accountsDispatch.invoiceDate).local().format('DD/MM/YYYY') : 'N/A'}
                                    </Typography>
                                  </Box>
                                </Grid>
                                {workProcessSummary.accountsDispatch.remarks && (
                                  <Grid size={{ xs: 12, md: 8 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Remarks
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.accountsDispatch.remarks}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                 {workProcessSummary.accountsDispatch?.createdBy && (
                                  <Grid size={{ xs: 4 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Created by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.accountsDispatch?.createdBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.accountsDispatch?.createdBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                 {workProcessSummary.accountsDispatch?.updatedBy && (
                                  <Grid size={{ xs: 4 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Updated by
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.accountsDispatch?.updatedBy.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.accountsDispatch?.updatedBy.empId}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                  {workProcessSummary.accountsDispatch?.companyId && (
                                  <Grid size={{ xs: 4 }}>
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                        Company Details
                                      </Typography>
                                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.accountsDispatch?.companyId.name}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.accountsDispatch?.companyId.mobile}
                                      </Typography>
                                           <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {workProcessSummary.accountsDispatch?.companyId.email}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                <Grid size={{ xs: 12, md: workProcessSummary.accountsDispatch.remarks ? 4 : 12 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: workProcessSummary.accountsDispatch.remarks ? 1 : 0 }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleEditWorkProcess(6, workProcessSummary.accountsDispatch)}
                                      startIcon={<EditIcon />}
                                       disabled={workProcessSummary.assembly?.[0]?.status === 'completed' && user?.role !=='admin'}
                                    >
                                      Edit
                                    </Button>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                No dispatch entry found.
                              </Typography>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleCreateWorkProcess(6)}
                                startIcon={<AddIcon />}
                              >
                                Start Process
                              </Button>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </>
                );
              })()}
            </Box>
          ) : (
            <Typography>No data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Work Process Form Dialog */}
      <Dialog 
        open={workProcessFormOpen} 
        onClose={() => setWorkProcessFormOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <FormikProvider value={workProcessFormik}>
          <form onSubmit={workProcessFormik.handleSubmit}>
            <DialogTitle>
              {editingWorkProcess ? 'Edit' : 'Create'} Work Process - Level {selectedWorkProcessLevel}
              {selectedWorkProcessLevel === 1 && ' (Punching Laser)'}
              {selectedWorkProcessLevel === 2 && ' (Bending Machine)'}
              {selectedWorkProcessLevel === 3 && ' (Welding & Grinding)'}
              {selectedWorkProcessLevel === 4 && ' (Powder Coating)'}
              {selectedWorkProcessLevel === 5 && ' (Assembly)'}
              {selectedWorkProcessLevel === 6 && ' (Accounts & Dispatch)'}
            </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={3}>
                {selectedWorkProcessLevel < 6 ? (
                  // Fields for levels 1-5 (manufacturing processes)
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        name="startDateTime"
                        label="Start Date & Time"
                        value={workProcessFormik.values.startDateTime}
                        onChange={workProcessFormik.handleChange}
                        onBlur={workProcessFormik.handleBlur}
                        error={workProcessFormik.touched.startDateTime && Boolean(workProcessFormik.errors.startDateTime)}
                        helperText={workProcessFormik.touched.startDateTime && workProcessFormik.errors.startDateTime}
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        name="endDateTime"
                        label="End Date & Time"
                        value={workProcessFormik.values.endDateTime}
                        onChange={workProcessFormik.handleChange}
                        onBlur={workProcessFormik.handleBlur}
                        error={workProcessFormik.touched.endDateTime && Boolean(workProcessFormik.errors.endDateTime)}
                        helperText={workProcessFormik.touched.endDateTime && workProcessFormik.errors.endDateTime}
                        disabled={workProcessFormik.values.status !== 'completed'}
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        select
                        name="status"
                        label="Status"
                        value={workProcessFormik.values.status}
                        onChange={workProcessFormik.handleChange}
                        onBlur={workProcessFormik.handleBlur}
                        error={workProcessFormik.touched.status && Boolean(workProcessFormik.errors.status)}
                        helperText={workProcessFormik.touched.status && workProcessFormik.errors.status}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="progress">In Progress</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      {/* Empty grid for spacing */}
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h6" >Materials Used</Typography>
                      <Typography variant="caption"  sx={{ mb: 2 }}>If you are using existing material, select the material and enter 0 for count and 0 for kg.</Typography>
                      <FieldArray name="materialsUsed">
                        {({ push, remove }) => (
                          <Box mt={1}>
                            {workProcessFormik.values.materialsUsed.map((material, index) => (
                              <Paper key={index} sx={{ p: 2, mb: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                  <Grid size={{ xs: 12, sm: 4 }}>
                                    <Autocomplete
                                      options={materials.filter(m => {
                                        // Filter out materials that are already selected in other rows
                                        const isAlreadySelected = workProcessFormik.values.materialsUsed.some(
                                          (usedMaterial, i) => i !== index && usedMaterial.materialId === m._id
                                        );
                                        return !isAlreadySelected;
                                      })}
                                      getOptionLabel={(option) => typeof option === 'object' ? `${option.materialName} ${option.jobType ? ` - ${option.jobType}` : ''}` : ''}
                                      value={materials.find(m => m._id === material.materialId) || null}
                                      onChange={(_, newValue) => {
                                        workProcessFormik.setFieldValue(`materialsUsed.${index}.materialId`, newValue?._id || '');
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Material"
                                          fullWidth
                                          required
                                          name={`materialsUsed.${index}.materialId`}
                                          error={Boolean(
                                            workProcessFormik.touched.materialsUsed?.[index]?.materialId &&
                                            workProcessFormik.errors.materialsUsed?.[index] &&
                                            typeof workProcessFormik.errors.materialsUsed[index] === 'object' &&
                                            (workProcessFormik.errors.materialsUsed[index] as any)?.materialId
                                          )}
                                          helperText={
                                            workProcessFormik.touched.materialsUsed?.[index]?.materialId &&
                                            workProcessFormik.errors.materialsUsed?.[index] &&
                                            typeof workProcessFormik.errors.materialsUsed[index] === 'object'
                                              ? (workProcessFormik.errors.materialsUsed[index] as any)?.materialId
                                              : ''
                                          }
                                        />
                                      )}
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 3 }}>
                                    <TextField
                                      fullWidth
                                      type="number"
                                      name={`materialsUsed.${index}.quantityCount`}
                                      label="Quantity (Count)"
                                      value={material.quantityCount}
                                      onChange={workProcessFormik.handleChange}
                                      onBlur={workProcessFormik.handleBlur}
                                      error={Boolean(
                                        workProcessFormik.touched.materialsUsed?.[index]?.quantityCount && 
                                        workProcessFormik.errors.materialsUsed?.[index] && 
                                        typeof workProcessFormik.errors.materialsUsed[index] === 'object' &&
                                        (workProcessFormik.errors.materialsUsed[index] as any)?.quantityCount
                                      )}
                                      helperText={
                                        workProcessFormik.touched.materialsUsed?.[index]?.quantityCount && 
                                        workProcessFormik.errors.materialsUsed?.[index] && 
                                        typeof workProcessFormik.errors.materialsUsed[index] === 'object' 
                                          ? (workProcessFormik.errors.materialsUsed[index] as any)?.quantityCount 
                                          : ''
                                      }
                                      slotProps={{ htmlInput: { min: 0, step: 1 } }}
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 3 }}>
                                    <TextField
                                      fullWidth
                                      type="number"
                                      name={`materialsUsed.${index}.quantityKg`}
                                      label="Quantity (Kg)"
                                      value={material.quantityKg}
                                      onChange={workProcessFormik.handleChange}
                                      onBlur={workProcessFormik.handleBlur}
                                      error={Boolean(
                                        workProcessFormik.touched.materialsUsed?.[index]?.quantityKg && 
                                        workProcessFormik.errors.materialsUsed?.[index] && 
                                        typeof workProcessFormik.errors.materialsUsed[index] === 'object' &&
                                        (workProcessFormik.errors.materialsUsed[index] as any)?.quantityKg
                                      )}
                                      helperText={
                                        workProcessFormik.touched.materialsUsed?.[index]?.quantityKg && 
                                        workProcessFormik.errors.materialsUsed?.[index] && 
                                        typeof workProcessFormik.errors.materialsUsed[index] === 'object' 
                                          ? (workProcessFormik.errors.materialsUsed[index] as any)?.quantityKg 
                                          : ''
                                      }
                                      slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 2 }}>
                                    <IconButton
                                      color="error"
                                      onClick={() => remove(index)}
                                      disabled={workProcessFormik.values.materialsUsed.length <= 1}
                                      title="Remove Material"
                                    >
                                      <RemoveIcon />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </Paper>
                            ))}
                            <Button
                              startIcon={<AddIcon />}
                              onClick={() => {
                                // Check if there's already an empty material row
                                const hasEmptyMaterial = workProcessFormik.values.materialsUsed.some(
                                  (m) => !m.materialId || m.materialId === ''
                                );
                                if (hasEmptyMaterial) {
                                  // alert('Please select a material for the existing row before adding a new one.');
                                  toast.error('Please select a material for the existing row before adding a new one.');
                                  return;
                                }
                                push({ materialId: '', quantityCount: 0, quantityKg: 0 });
                              }}
                              variant="outlined"
                              size="small"
                            >
                              Add Material
                            </Button>
                          </Box>
                        )}
                      </FieldArray>
                    </Grid>
                  </>
                ) : (
                  // Fields for level 6 (dispatch)
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        name="dispatchedDateTime"
                        label="Dispatched Date & Time"
                        required
                        value={workProcessFormik.values.dispatchedDateTime}
                        onChange={workProcessFormik.handleChange}
                        onBlur={workProcessFormik.handleBlur}
                        error={workProcessFormik.touched.dispatchedDateTime && Boolean(workProcessFormik.errors.dispatchedDateTime)}
                        helperText={workProcessFormik.touched.dispatchedDateTime && workProcessFormik.errors.dispatchedDateTime}
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        name="vehicleNumber"
                        label="Vehicle Number"
                        required

                        value={workProcessFormik.values.vehicleNumber}
                        onChange={workProcessFormik.handleChange}
                        onBlur={workProcessFormik.handleBlur}
                        error={workProcessFormik.touched.vehicleNumber && Boolean(workProcessFormik.errors.vehicleNumber)}
                        helperText={workProcessFormik.touched.vehicleNumber && workProcessFormik.errors.vehicleNumber}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        name="invoiceNumber"
                        label="Invoice Number"
                        required

                        value={workProcessFormik.values.invoiceNumber}
                        onChange={workProcessFormik.handleChange}
                        onBlur={workProcessFormik.handleBlur}
                        error={workProcessFormik.touched.invoiceNumber && Boolean(workProcessFormik.errors.invoiceNumber)}
                        helperText={workProcessFormik.touched.invoiceNumber && workProcessFormik.errors.invoiceNumber}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        type="date"
                        name="invoiceDate"
                        label="Invoice Date"
                        required

                        value={workProcessFormik.values.invoiceDate}
                        onChange={workProcessFormik.handleChange}
                        onBlur={workProcessFormik.handleBlur}
                        error={workProcessFormik.touched.invoiceDate && Boolean(workProcessFormik.errors.invoiceDate)}
                        helperText={workProcessFormik.touched.invoiceDate && workProcessFormik.errors.invoiceDate}
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="remarks"
                    label="Remarks (Optional)"

                    value={workProcessFormik.values.remarks}
                    onChange={workProcessFormik.handleChange}
                    onBlur={workProcessFormik.handleBlur}
                    error={workProcessFormik.touched.remarks && Boolean(workProcessFormik.errors.remarks)}
                    helperText={workProcessFormik.touched.remarks && workProcessFormik.errors.remarks}
                    placeholder="Any additional notes..."
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWorkProcessFormOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!workProcessFormik.isValid || workProcessFormik.isSubmitting}
            >
              {workProcessFormik.isSubmitting ? (
                <CircularProgress size={20} />
              ) : (
                editingWorkProcess ? 'Update' : 'Create'
              )}
            </Button>
          </DialogActions>
          </form>
        </FormikProvider>
      </Dialog>

      <CustomDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filter Purchase Orders"
        width={400}
        anchor="right"
        actions={
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
            startIcon={<FilterIcon />}
            fullWidth
          >
            Clear Filters
          </Button>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            label="Filter by Customer"
            variant="outlined"
            size="small"
            fullWidth
            value={customerIdFilter}
            onChange={(e) => setCustomerIdFilter(e.target.value)}
          >
            <MenuItem value="">All Customers</MenuItem>
            {customers.map((customer) => (
              <MenuItem key={customer._id} value={customer._id}>
                {customer.companyName}
              </MenuItem>
            ))}
          </TextField>
          
          <TextField
            select
            label="Filter by Status"
            variant="outlined"
            size="small"
            fullWidth
            value={isActiveFilter}
            onChange={(e) => setIsActiveFilter(e.target.value === '' ? '' : e.target.value === 'true')}
            slotProps={{
              select: {
                native: true,
              },
            }}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </TextField>
          
          {activeFilterCount > 0 && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied
              </Typography>
            </Box>
          )}
        </Box>
      </CustomDrawer>

      {/* Invoice Dialog */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Generate Invoice</Typography>
            {selectedPOForInvoice && (
              <Chip
                label={selectedPOForInvoice.poNumber}
                color="primary"
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPOForInvoice && (
            <EditableInvoiceForm
              purchaseOrder={selectedPOForInvoice}
              comprehensiveData={comprehensivePOData}
              initialData={existingInvoiceData}
              onSave={async (invoiceData) => {
                console.log('=== Invoice Ready to Save to Backend ===');
                console.log('Database References:', {
                  customerId: invoiceData.customerId,
                  companyId: invoiceData.companyId,
                  purchaseOrderId: invoiceData.purchaseOrderId,
                  comprehensiveDataId: invoiceData.comprehensiveDataId,
                });
                console.log('Invoice Items Details:', invoiceData.items.map(item => ({
                  materialId: item.materialId,
                  companyId: item.companyId,
                  description: item.description,
                  hsn: item.hsn,
                  qty: item.qty,
                  unit: item.unit,
                  rate: item.rate,
                  amount: item.amount,
                })));
                console.log('Invoice Totals:', {
                  subTotal: invoiceData.subTotal,
                  cgstTotal: invoiceData.cgstTotal,
                  sgstTotal: invoiceData.sgstTotal,
                  grandTotal: invoiceData.grandTotal,
                  totalInWords: invoiceData.totalInWords,
                });
                console.log('Full Invoice Data JSON:', JSON.stringify(invoiceData, null, 2));

                // Save invoice to backend
                try {
                  let response;
                  if (invoiceData._id) {
                    // Update existing invoice
                    response = await purchaseOrderService.updateInvoice(invoiceData._id, invoiceData);
                  } else {
                    // Create new invoice
                    response = await purchaseOrderService.createInvoice(invoiceData);
                  }

                  if (response.success) {
                    toast.success(invoiceData._id ? 'Invoice updated successfully!' : 'Invoice created successfully!');
                    setInvoiceDialogOpen(false);
                  } else {
                    toast.error(response.message || 'Failed to save invoice');
                  }
                } catch (error) {
                  console.error('Error saving invoice:', error);
                  toast.error('Error saving invoice');
                }
              }}
              onCancel={() => setInvoiceDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Backdrop
        sx={{ color: '#fff', zIndex: 99999 }}
        open={apiLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};